import { memo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Controller, type Control } from "react-hook-form";

import type { QuestionnaireFormValues } from "../types";

export interface QuestionsActionsCardProps {
  control: Control<QuestionnaireFormValues>;
  Controller: typeof Controller;
  isConfigCollapsed: boolean;
  onToggleCollapsed: () => void;
}

const QuestionsActionsCard = memo(
  ({
    control,
    Controller,
    isConfigCollapsed,
    onToggleCollapsed,
  }: QuestionsActionsCardProps) => (
    <Card
      variant="outlined"
      sx={{ width: "100%", flexShrink: 0 }}
    >
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Configurações
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
          >
            <Button
              variant="text"
              startIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: isConfigCollapsed
                      ? "rotate(-90deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              }
              onClick={onToggleCollapsed}
              sx={{ whiteSpace: "nowrap", textTransform: "none" }}
            >
              {isConfigCollapsed ? "Editar configurações" : "Recolher seção"}
            </Button>
          </Stack>
        </Stack>

        {!isConfigCollapsed && (
          <>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Controller
                name="title"
                control={control}
                rules={{
                  validate: (value) =>
                    (typeof value === "string" && value.trim().length > 0) ||
                    "Preencha o título do questionário",
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Título do questionário"
                    fullWidth
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Descrição"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    {...field}
                  />
                )}
              />
              <Box>
                <Typography variant="body1" fontWeight={500} sx={{ mb: 2 }}>
                  Categoria
                </Typography>
                <Controller
                  name="usageType"
                  control={control}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      exclusive
                      value={field.value}
                      onChange={(_e, v) => v && field.onChange(v)}
                      aria-label="tipo de questionário (triagem, pré, pós ou acompanhamento)"
                      sx={{ flexWrap: "wrap", gap: 0.5 }}
                    >
                      <ToggleButton value="triage" aria-label="Triagem">
                        Triagem
                      </ToggleButton>
                      <ToggleButton
                        value="preConsultation"
                        aria-label="Pré-consulta"
                      >
                        Pré-Consulta
                      </ToggleButton>
                      <ToggleButton
                        value="postConsultation"
                        aria-label="Pós-consulta"
                      >
                        Pós-Consulta
                      </ToggleButton>
                      <ToggleButton value="attendance" aria-label="Acompanhamento">
                        Acompanhamento
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Box>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  ),
);

QuestionsActionsCard.displayName = "QuestionsActionsCard";

export default QuestionsActionsCard;
